import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import type { Talent } from './models'

export type RosterEntryRow =
  Database['public']['Tables']['roster_entries']['Row']

export interface RosterEntry extends RosterEntryRow {
  models: Talent
}

export const rosterEntry = {
  findManyByWorkspace: (workspaceId: string) =>
    queryOptions({
      queryKey: ['roster-entries', 'workspace', workspaceId],
      queryFn: async (): Promise<RosterEntry[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch a roster without a workspace ID'
          )
        }

        const { data, error } = await supabase
          .from('roster_entries')
          .select('*, models!inner(*, model_shots(*))')
          .eq('workspace_id', workspaceId)
          .is('models.deleted_at', null)
          // Newest first, so a talent just added or created leads the roster
          .order('created_at', { ascending: false })

        if (error) {
          throw new Error(`Failed to fetch roster: ${error.message}`, {
            cause: error,
          })
        }

        return data as RosterEntry[]
      },
    }),

  /** How many rosters the talent is in, counting only rosters the user can see. */
  countByModel: (modelId: string) =>
    queryOptions({
      queryKey: ['roster-entries', 'model', modelId, 'count'],
      queryFn: async (): Promise<number> => {
        if (!modelId || modelId.trim() === '') {
          throw new Error(
            'Invalid modelId: cannot count rosters without a model ID'
          )
        }

        const { count, error } = await supabase
          .from('roster_entries')
          .select('id', { count: 'exact', head: true })
          .eq('model_id', modelId)

        if (error) {
          throw new Error(`Failed to count rosters: ${error.message}`, {
            cause: error,
          })
        }

        return count ?? 0
      },
    }),

  addMany: async (
    workspaceId: string,
    modelIds: string[],
    addedBy: string
  ): Promise<void> => {
    if (modelIds.length === 0) {
      return
    }

    const { error } = await supabase.from('roster_entries').upsert(
      modelIds.map((modelId) => ({
        workspace_id: workspaceId,
        model_id: modelId,
        added_by: addedBy,
      })),
      { onConflict: 'workspace_id,model_id', ignoreDuplicates: true }
    )

    if (error) {
      throw new Error(`Failed to add talents to roster: ${error.message}`, {
        cause: error,
      })
    }
  },

  remove: async (workspaceId: string, modelId: string): Promise<void> => {
    const { error } = await supabase
      .from('roster_entries')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('model_id', modelId)

    if (error) {
      throw new Error(`Failed to remove talent from roster: ${error.message}`, {
        cause: error,
      })
    }
  },
}
